import { useFragment } from '@apollo/client'
import { DeepPartial } from '@apollo/client/utilities'
import {
  Box,
  CollapsableCard,
  Icon,
  List,
  RadioButton,
  Text,
} from '@factorialco/deprecated-design-system'
import {
  PublishDialogSurveyFragment,
  PublishDialogSurveyFragmentDoc,
} from 'generated/resources/graphql'
import moment from 'moment'

import DayPicker from 'modules/core/components/DayPicker'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { firstDayOfWeekAsIndex } from 'modules/core/lib/firstDayOfWeek'
import i18n from 'modules/core/lib/i18n'

import { RecurrenceSelect } from 'modules/engagement_surveys/components/RecurrenceSelect'
import { StartTimeSelect } from 'modules/engagement_surveys/components/StartTimeSelect'
import type { WizardStepProps } from 'modules/engagement_surveys/components/SurveyWizard/types'

interface PublishStepProps extends WizardStepProps {
  surveyId: number
}

export function PublishStep({ data, errors, onDataChanged, surveyId }: PublishStepProps) {
  const {
    access: { firstDayOfWeek },
  } = useCurrentSession()

  const { data: survey } = useFragment({
    fragment: PublishDialogSurveyFragmentDoc,
    fragmentName: 'PublishDialogSurvey',
    from: {
      __typename: 'SurveysSurvey',
      id: surveyId,
    },
  })

  const isScheduled = !!data.startDate
  const publishErrors = getPublishValidationErrors(survey)

  return (
    <Box gap="s32">
      <Text.Header2 weight="semibold">
        {i18n.t('surveys.configuration.publish_settings.title')}
      </Text.Header2>

      <Box gap="s16">
        <CollapsableCard
          open={false}
          Content={<></>}
          Card={
            <Box flexDirection="row" justifyContent="spaceBetween">
              <Text.Normal weight="medium">
                {i18n.t('surveys.publish_dialog.launch_now')}
              </Text.Normal>

              <RadioButton
                size="medium"
                value={i18n.t('surveys.publish_dialog.launch_now')}
                onChange={() =>
                  onDataChanged({ ...data, startDate: undefined, startTime: undefined })
                }
                checked={!isScheduled}
              />
            </Box>
          }
        />
        <CollapsableCard
          open={isScheduled}
          Card={
            <Box flexDirection="row" justifyContent="spaceBetween">
              <Text.Normal weight="medium">{i18n.t('surveys.publish_dialog.schedule')}</Text.Normal>

              <RadioButton
                size="medium"
                value={i18n.t('surveys.publish_dialog.schedule')}
                onChange={() =>
                  onDataChanged({ ...data, startDate: moment().format('YYYY-MM-DD') })
                }
                checked={isScheduled}
              />
            </Box>
          }
          Content={
            <Box gap="s32">
              <Box flexDirection="row" alignItems="center" gap="s32" width="full">
                <DayPicker
                  withNull
                  label={i18n.t('forms.schedule.start_date')}
                  value={data.startDate}
                  onChange={(date) => {
                    onDataChanged({ ...data, startDate: date })
                  }}
                  error={errors.startDate}
                  minDate={new Date()}
                  firstDayOfWeek={firstDayOfWeekAsIndex(firstDayOfWeek)}
                  valueFormat="backend"
                />

                <StartTimeSelect
                  value={data.startTime}
                  onChange={(startTime) => {
                    onDataChanged({ ...data, startTime })
                  }}
                  date={data.startDate}
                />
              </Box>
            </Box>
          }
        />
        <Box gap="s16">
          <RecurrenceSelect
            value={data.recurrenceType || ('onetime' as const)}
            onChange={(recurrenceType) => {
              onDataChanged({ ...data, recurrenceType })
            }}
          />

          <Box flexDirection="row" alignItems="flexStart" gap="s32" width="full">
            <Box gap="s8" width="full">
              <DayPicker
                label={i18n.t('forms.schedule.end_date')}
                value={data.endsAt}
                onChange={(endsAt) => {
                  onDataChanged({ ...data, endsAt })
                }}
                error={errors.endsAt}
                minDate={data.startDate ? new Date(data.startDate) : new Date()}
                firstDayOfWeek={firstDayOfWeekAsIndex(firstDayOfWeek)}
                valueFormat="backend_datetime"
              />
              <Text.Normal size="200" color="grey700">
                {i18n.t('forms.schedule.end_date_description')}
              </Text.Normal>
            </Box>

            {data.recurrenceType !== 'onetime' ? (
              <TextField
                label={i18n.t('forms.schedule.days_to_answer')}
                value={
                  data.daysToAnswer && isFinite(data.daysToAnswer)
                    ? data.daysToAnswer?.toString()
                    : ''
                }
                onChange={(value) => {
                  const daysToAnswer = value && isFinite(Number(value)) ? Number(value) : undefined
                  onDataChanged({ ...data, daysToAnswer })
                }}
                error={errors.daysToAnswer}
              />
            ) : (
              <Box width="full" />
            )}
          </Box>
        </Box>

        {publishErrors.length > 0 && (
          <Box minWidth="s600">
            <List>
              {publishErrors.map((error) => (
                <List.Item key={error}>
                  <Box gap="s16" width="full" flexDirection="row" alignItems="center">
                    <Icon.Medium color="radicalRed1000" icon="cross" />
                    {error}
                  </Box>
                </List.Item>
              ))}
            </List>
          </Box>
        )}
      </Box>
    </Box>
  )
}

function getPublishValidationErrors(survey: DeepPartial<PublishDialogSurveyFragment>) {
  const errors = []

  if (survey?.form?.elementsConnection?.totalCount === 0) {
    errors.push(i18n.t('surveys.publish_dialog.errors.no_elements'))
  }

  return errors
}
